/**
 * Announces the values `useFormCacheSync` writes into the simulation form, so
 * auto-save can take them as the server's instead of as edits (shipcast-d8r.40).
 *
 * Without this, a value arriving from the server — a refresh, another tab —
 * looked to auto-save like something the user typed, and was PATCHed back about
 * 1.5 s later, possibly over a newer value. The sync knows exactly which fields
 * it wrote, so it says so; nothing has to be inferred by comparing values.
 */

import { createContext, useContext, useEffect } from 'react';

/** A form path the cache sync wrote (`teams.2.wip`, or a whole `teams`) and the value. */
export type SyncedFieldListener = (path: string, value: unknown) => void;

export interface FormSyncEvents {
  emit: SyncedFieldListener;
  /** Returns the unsubscribe function. */
  subscribe: (listener: SyncedFieldListener) => () => void;
}

export function createFormSyncEvents(): FormSyncEvents {
  const listeners = new Set<SyncedFieldListener>();
  return {
    emit: (path, value) => {
      for (const listener of listeners) listener(path, value);
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/** Provided around the simulation form by the page that runs the cache sync. */
export const FormSyncContext = createContext<FormSyncEvents | null>(null);

/** Call `listener` for every value the cache sync writes, while mounted. */
export function useSyncedFields(listener: SyncedFieldListener): void {
  const events = useContext(FormSyncContext);
  useEffect(() => events?.subscribe(listener), [events, listener]);
}
